import { useEffect, useRef, useState } from 'react';

interface UseWakeLockResult {
  isSupported: boolean;
  isActive: boolean;
}

export function useWakeLock(): UseWakeLockResult {
  const [isSupported] = useState(() => typeof navigator !== 'undefined' && 'wakeLock' in navigator);
  const [isActive, setIsActive] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!isSupported) {
      return;
    }

    async function acquire(): Promise<void> {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        sentinelRef.current = sentinel;
        setIsActive(true);
        sentinel.addEventListener('release', () => {
          setIsActive(false);
        });
      } catch {
        setIsActive(false);
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        void acquire();
      }
    }

    void acquire();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      const sentinel = sentinelRef.current;
      if (sentinel !== null && !sentinel.released) {
        void sentinel.release();
      }
    };
  }, [isSupported]);

  return { isSupported, isActive };
}
